import isPlainObject from "is-plain-obj";
import { forEach, has, isEqual, keys, memoize, noop, set } from "lodash";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { UnpackNestedValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Subject } from "rxjs";
import { debounceTime, filter, map, withLatestFrom } from "rxjs/operators";
import { object } from "yup";
import { flattenObj } from "../../../store/common/forms";
import { useYupValidationResolver } from "../../../utils/utils";
import { ErrorMap, Field, UseFormOptions, UseFormReturn } from "./models";

export function useForm<
  AllFields extends Record<PropertyKey, any>,
  FieldNames extends PropertyKey = keyof AllFields
>({
  defaultValues,
  validationSchema,
}: UseFormOptions<AllFields, FieldNames>): UseFormReturn<
  AllFields,
  FieldNames
> {
  const { t } = useTranslation("common");

  const flattenedDefaultValues = useMemo(() => {
    return defaultValues ? flattenObj(defaultValues) : {};
  }, [defaultValues]);

  const [values, setValues] = useState<AllFields>(
    flattenedDefaultValues as AllFields
  );

  // We have to reset the values if default values changed othewise the persisted changes from
  // above will not be reflected.
  // checking for equality because if we're getting default values before setting values because
  // this might be a child form, hence default values might come from parent form. And we will end up
  // setting the parent values (i.e. infinite cycle)
  const defaultValuesUpdated = useMemo(() => new Subject<AllFields>(), []);
  const valuesUpdated = useMemo(() => new Subject<AllFields>(), []);

  useEffect(() => {
    const sub = defaultValuesUpdated
      .pipe(
        debounceTime(100),
        withLatestFrom(valuesUpdated),
        filter(([defaults, values]) => !isEqual(defaults, values)),
        map(([defaults]) => defaults)
      )
      .subscribe((x) => setValues(x));
    return () => sub.unsubscribe();
  }, [defaultValuesUpdated, valuesUpdated]);

  useEffect(() => {
    valuesUpdated.next(values);
  }, [values]);

  useEffect(() => {
    defaultValuesUpdated.next(flattenedDefaultValues as AllFields);
  }, [flattenedDefaultValues]);

  const [errors, setErrors] = useState<ErrorMap<FieldNames>>({});
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);

  const validate = useYupValidationResolver(
    useMemo(() => validationSchema ?? object(), [validationSchema]),
    useMemo(
      () => ({
        strict: false,
        abortEarly: false,
      }),
      []
    )
  );

  const computedValues = useMemo(() => {
    const foo = {};
    keys(values).forEach((key) => {
      set(foo, key, values[key]);
    });
    return foo as UnpackNestedValue<AllFields>;
  }, [values, set]);

  const handleChange = useMemo(
    () =>
      memoize((field: FieldNames) => (value: any) => {
        setValues((prevValues) => {
          const newFieldValue = has(value, "target")
            ? value.target?.value
            : value;
          let newVals = prevValues;

          if (isPlainObject(newFieldValue)) {
            const newObj = flattenObj(newFieldValue);

            forEach(newObj, (val, key) => {
              newVals = { ...newVals, [`${String(field)}.${key}`]: val };
            });
          } else {
            newVals = { ...newVals, [field]: newFieldValue };
          }

          return newVals;
        });
      }),
    [setValues]
  );

  // Will return if there the form is valid
  const trigger = useCallback(async () => {
    if (validationSchema) {
      const result = await validate(computedValues);
      if (Object.keys(result.errors).length > 0) {
        setErrors(result.errors);
        return false;
      } else {
        setErrors({});
      }
    }
    return true;
  }, [validationSchema, validate, computedValues, setErrors]);

  // re-validate field after it's submitted
  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [trigger, isSubmitted]);

  const handleSubmit: (
    callback: (data: AllFields) => Promise<any>,
    options?: {
      throwError: boolean;
    }
  ) => (e?: SyntheticEvent) => Promise<any> = useCallback(
    (callback, options) => async (e) => {
      if (e) e.preventDefault();
      setIsSubmitted(true);
      if (validationSchema) {
        const result = await validate(computedValues);
        if (Object.keys(result.errors).length > 0) {
          setErrors(result.errors);
          if (options?.throwError) throw new Error(t("form-invalid"));
          return;
        } else {
          setErrors({});
        }
      }

      options?.throwError
        ? await callback(computedValues)
        : await callback(computedValues).catch(noop);
      setIsSubmitSuccessful(true);
    },
    [computedValues, validationSchema, setErrors, validate]
  );
  const invalid = useMemo(() => keys(errors).length > 0, [errors]);

  const register: (
    fieldName: FieldNames
  ) => Field<AllFields[FieldNames]> = useMemo(
    () =>
      memoize((fieldName: FieldNames) => ({
        onChange: handleChange(fieldName),
        value: values[fieldName],
        error: !!errors[fieldName],
        helperText: errors[fieldName]?.message,
      })),
    [handleChange, values, errors]
  );

  return {
    handleChange,
    handleSubmit,
    errors,
    isSubmitted,
    values: computedValues,
    isSubmitSuccessful,
    trigger,
    invalid,
    register,
    setErrors,
  };
}
